import React, { ReactNode, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { FiltersPanel } from 'components/shared/FiltersPanel';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { ProposalsMetricsDashboard } from '../dashboard/ProposalsMetricsDashboard';
import { ProposalsViewTypes } from '../../types';
import { HubTable } from '../tables/hub/HubTable';
import { hubTableApiQueryByType } from '../../constants';
import { hubSearchFilter } from '../../lib/hubSearchFilters';
import { HubProposal, HubProposalResponse } from '../../api/types';
import { ProposalsTypeManager } from '../components/ProposalsTypeManager';
import { useAccessControlProposalHubTable } from '../../hooks/useAccessControlProposalHubTable';

export const ProposalsPage = () => {
  const history = useHistory();

  const { hasAccessToAllProposals } = useAccessControlProposalHubTable();

  const dynamicsAccountId = useSelector((state: any) => state.account.dynamicsAccountId);

  const [mainSearchValue, setMainSearchValue] = useState<string>('');
  const [customTableKey, setCustomTableKey] = useState<number>(Date.now());
  const [proposalsType, setProposalsType] = useState<ProposalsViewTypes>(ProposalsViewTypes.My);

  const { data: proposalsHubData, loading: isProposalsLoading } = useQuery<HubProposalResponse>(
    hubTableApiQueryByType[proposalsType],
    { nextFetchPolicy: 'cache-first', fetchPolicy: 'cache-and-network' }
  );

  const handleGoToProposal = (proposal: HubProposal) => {
    window.open(`/proposalDetail/${proposal.id}?from=/proposals-hub`, '_blank');
  };

  const handleGoToProposalDetail = (proposal: HubProposal) => {
    if (!proposal.id || !dynamicsAccountId) return;

    const link = generateProposalPageURL(proposal.id, dynamicsAccountId);
    window.open(link, '_blank');
  };

  const handleEditProposal = (proposal: HubProposal) => {
    history.push({
      hash: '#desking',
      pathname: `/vo/${proposal.vendorOpportunityId}`,
      state: {
        proposalId: proposal.id,
        proposalSaleType: proposal.saleType,
      },
    });
  };

  const filteredBySearchTableData = useMemo(
    () => hubSearchFilter(proposalsHubData?.proposals ?? [], mainSearchValue),
    [mainSearchValue, proposalsHubData?.proposals]
  );

  const renderTabContentByType: Record<ProposalsViewTypes, ReactNode> = {
    [ProposalsViewTypes.My]: (
      <>
        <FiltersPanel
          withReset
          disabled={isProposalsLoading}
          onSearchChange={(filter, nextValue) => {
            setMainSearchValue(nextValue);
          }}
          searchInputProps={{
            placeholder: 'Start searching proposals by customer...',
          }}
          extraCbForReset={() => {
            setCustomTableKey(Date.now());
          }}
        />

        <HubTable
          key={customTableKey}
          loading={isProposalsLoading}
          onEditProposal={handleEditProposal}
          onGoToProposal={handleGoToProposal}
          tableData={filteredBySearchTableData}
          onGoToProposalDetail={handleGoToProposalDetail}
        />
      </>
    ),
    [ProposalsViewTypes.All]: (
      <>
        <FiltersPanel
          withReset
          disabled={isProposalsLoading}
          onSearchChange={(filter, nextValue) => {
            setMainSearchValue(nextValue);
          }}
          searchInputProps={{
            placeholder: 'Start searching proposals by customer...',
          }}
          extraCbForReset={() => {
            setCustomTableKey(Date.now());
          }}
        />

        <HubTable
          key={customTableKey}
          loading={isProposalsLoading}
          onEditProposal={handleEditProposal}
          onGoToProposal={handleGoToProposal}
          tableData={filteredBySearchTableData}
          onGoToProposalDetail={handleGoToProposalDetail}
        />
      </>
    ),
    [ProposalsViewTypes.Dashboard]: <ProposalsMetricsDashboard />,
  };

  return (
    <PageContainer>
      <CardContainer>
        <Stack gap="1rem" height="100%">
          <RenderWithCondition condition={hasAccessToAllProposals}>
            <ProposalsTypeManager currentType={proposalsType} onTypeChange={setProposalsType} />
          </RenderWithCondition>

          {renderTabContentByType[proposalsType]}
        </Stack>
      </CardContainer>
    </PageContainer>
  );
};
